/**
 * `GET /api/deadlines/rule` — the response period this office has on file.
 *
 * Why the New Case screen asks for it
 * ------------------------------------
 * On an Indian refusal the *Deadline as printed* box comes up empty and stays
 * empty, because India states its time limit as a period ("one month from the
 * date of receipt") rather than a date. The screen said nothing further, so an
 * attorney could not tell a reading that failed from a document with no date
 * on it — and the ask was to fall back to a flat thirty days so the flow keeps
 * moving.
 *
 * The flow already moves: a missing deadline stopped blocking an examination
 * run, and it is the pack gate, not the run gate, that holds a draft until a
 * person signs a date. What was missing is the arithmetic, which the platform
 * already had — `IN_EXAM_OA_V1`, one month under Trade Marks Rules 2017
 * r. 33(1). Given the date on the report, this route applies it.
 *
 * **Thirty days is deliberately not the fallback.** India's period is one
 * calendar *month*, and the two are not interchangeable: one month from 31
 * January is 28 days, from 31 March it is 30. And GB, US and EM have no
 * registered period at all — `server/app/deadlines/rules.py` holds that an
 * unsourced statutory period is worse than none, because a date on the screen
 * is a date somebody will work to. So `registered: false` is reported as an
 * absence and the box stays the attorney's to fill.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'
import type { Jurisdiction, ProceedingType } from './types'

/** `DeadlineRuleOut` — what the registry holds, and what it computed. */
export interface DeadlineRuleAnswer {
  registered: boolean
  rule_id: string | null
  period: number | null
  unit: string | null
  status: string | null
  authority: string | null
  runs_from: string | null
  consequence: string | null
  /** Present only when a `report_date` was sent *and* a rule is registered. */
  due_date: string | null
  /** The sentence to show, already written for a person. */
  detail: string
}

export const deadlineKeys = {
  rule: (office: string, proceeding: string, reportDate: string) =>
    ['deadlines', 'rule', office, proceeding, reportDate] as const,
}

/**
 * The rule for this office, applied to `reportDate` when one is given.
 *
 * Disabled until there is an office, so the confirm step does not fire a query
 * before it knows which registry to ask.
 */
export function useDeadlineRule(
  office: Jurisdiction | '',
  proceeding: ProceedingType,
  reportDate: string,
): UseQueryResult<DeadlineRuleAnswer> {
  return useQuery<DeadlineRuleAnswer>({
    queryKey: deadlineKeys.rule(office, proceeding, reportDate),
    enabled: office !== '',
    queryFn: () => {
      const params = new URLSearchParams({ office, proceeding })
      if (reportDate) params.set('report_date', reportDate)
      return api.get<DeadlineRuleAnswer>(`/api/deadlines/rule?${params.toString()}`)
    },
  })
}
